import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  GetUserCommand,
  GetTokensFromRefreshTokenCommand,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  SetUserMFAPreferenceCommand,
} from "@aws-sdk/client-cognito-identity-provider";

import {
  createSrpSession,
  createSecretHash,
  wrapInitiateAuth,
  signSrpSession,
  wrapAuthChallenge,
} from "cognito-srp-helper";


export async function srpLogin({
  region,
  userPoolId,
  clientId,
  clientSecret,
  username,
  password,
}) {
  try {
    const client = new CognitoIdentityProviderClient({ region });

    const secretHash =
      clientSecret && clientSecret.length
        ? createSecretHash(username, clientId, clientSecret)
        : undefined;

    const srpSession = createSrpSession(username, password, userPoolId, false);

    const initiateReq = wrapInitiateAuth(srpSession, {
      ClientId: clientId,
      AuthFlow: "USER_SRP_AUTH",
      AuthParameters: {
        USERNAME: username,
        ...(secretHash && { SECRET_HASH: secretHash }),
      },
    });

    const initiateRes = await client.send(
      new InitiateAuthCommand(initiateReq)
    );

    const signedSession = signSrpSession(srpSession, initiateRes);

    const challengeReq = wrapAuthChallenge(signedSession, {
      ClientId: clientId,
      ChallengeName: "PASSWORD_VERIFIER",
      ChallengeResponses: {
        USERNAME: username,
        ...(secretHash && { SECRET_HASH: secretHash }),
      },
      Session: initiateRes.Session,
    });

    const challengeRes = await client.send(
      new RespondToAuthChallengeCommand(challengeReq)
    );

    if (
      challengeRes.ChallengeName === "SOFTWARE_TOKEN_MFA" ||
      challengeRes.ChallengeName === "SMS_MFA"
    ) {
      return {
        status: "MFA_REQUIRED",
        challengeName: challengeRes.ChallengeName,
        session: challengeRes.Session,
        username:
          initiateRes.ChallengeParameters?.USER_ID_FOR_SRP ?? username,
      };
    }

    const result = challengeRes.AuthenticationResult;
    if (!result) {
      throw new Error("Authentication failed – no tokens returned");
    }

    return {
      status: "SUCCESS",
      idToken: result.IdToken,
      accessToken: result.AccessToken,
      refreshToken: result.RefreshToken,
      expiresIn: result.ExpiresIn,
      tokenType: result.TokenType,
    };
  } catch (error) {
    // Handle specific AWS Cognito errors
    if (error.name === 'UserNotFoundException') {
      throw new Error('User not found. Please check your username and try again.');
    }
    if (error.name === 'NotAuthorizedException') {
      throw new Error('Invalid username or password.');
    }
    if (error.name === 'InvalidParameterException') {
      throw new Error('Invalid login parameters. Please try again.');
    }
    if (error.name === 'TooManyRequestsException') {
      throw new Error('Too many login attempts. Please wait and try again later.');
    }
    if (error.name === 'UserNotConfirmedException') {
      throw new Error('Account not confirmed. Please check your email for verification instructions.');
    }

    // Re-throw other errors with a generic message
    throw new Error(`Authentication failed: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Complete an MFA challenge returned by srpLogin (status "MFA_REQUIRED").
 * Exchanges the 6-digit code + session for tokens.
 */
export async function respondToMfaChallenge({
  region,
  clientId,
  clientSecret,
  username,
  session,
  code,
  challengeName = "SOFTWARE_TOKEN_MFA",
}) {
  try {
    const client = new CognitoIdentityProviderClient({ region });

    const secretHash =
      clientSecret && clientSecret.length
        ? createSecretHash(username, clientId, clientSecret)
        : undefined;

    const codeKey =
      challengeName === "SMS_MFA" ? "SMS_MFA_CODE" : "SOFTWARE_TOKEN_MFA_CODE";

    const res = await client.send(
      new RespondToAuthChallengeCommand({
        ClientId: clientId,
        ChallengeName: challengeName,
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          [codeKey]: code,
          ...(secretHash && { SECRET_HASH: secretHash }),
        },
      })
    );

    const result = res.AuthenticationResult;
    if (!result) {
      throw new Error("MFA verification failed – no tokens returned");
    }

    return {
      status: "SUCCESS",
      idToken: result.IdToken,
      accessToken: result.AccessToken,
      refreshToken: result.RefreshToken,
      expiresIn: result.ExpiresIn,
      tokenType: result.TokenType,
    };
  } catch (error) {
    if (error.name === "CodeMismatchException") {
      throw new Error("Incorrect authentication code. Please try again.");
    }
    if (error.name === "ExpiredCodeException") {
      throw new Error("The code has expired. Please try again.");
    }
    if (error.name === "NotAuthorizedException") {
      throw new Error("Your session has expired. Please sign in again.");
    }
    throw new Error(
      `MFA verification failed: ${error.message || "Unknown error"}`
    );
  }
}

export async function refreshTokens({
  region,
  clientId,
  refreshToken,
  clientSecret,   // optional, if your app client uses a secret
  deviceKey,      // optional, if you're using remembered devices
}) {
  const client = new CognitoIdentityProviderClient({ region });

  const res = await client.send(
    new GetTokensFromRefreshTokenCommand({
      ClientId: clientId,
      RefreshToken: refreshToken,
      ...(clientSecret && { ClientSecret: clientSecret }),
      ...(deviceKey && { DeviceKey: deviceKey }),
    })
  );

  const result = res.AuthenticationResult;
  if (!result) {
    throw new Error("Token refresh failed – no tokens returned");
  }


  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    // If Cognito doesn't send a new refresh token, keep the original one
    refreshToken: result.RefreshToken ?? refreshToken,
    expiresIn: result.ExpiresIn,
    tokenType: result.TokenType,
  };
}

// TOTP enrollment: request a software-token secret from Cognito
export async function associateSoftwareToken({ region, accessToken, issuer = "Vantage", accountLabel }) {
  const client = new CognitoIdentityProviderClient({ region });

  const res = await client.send(
    new AssociateSoftwareTokenCommand({ AccessToken: accessToken })
  );

  const secretCode = res.SecretCode;
  const label = encodeURIComponent(`${issuer}:${accountLabel ?? "account"}`);
  const otpauthUri = `otpauth://totp/${label}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;

  return { secretCode, otpauthUri };
}

// TOTP verification and  enable: verify the 6-digit code from the authenticator
export async function verifyAndEnableSoftwareToken({
  region,
  accessToken,
  code,
  friendlyDeviceName = "Authenticator app",
}) {
  try {
    const client = new CognitoIdentityProviderClient({ region });

    const verifyRes = await client.send(
      new VerifySoftwareTokenCommand({
        AccessToken: accessToken,
        UserCode: code,
        FriendlyDeviceName: friendlyDeviceName,
      })
    );

    if (verifyRes.Status !== "SUCCESS") {
      throw new Error("The code could not be verified. Please try again.");
    }

    await client.send(
      new SetUserMFAPreferenceCommand({
        AccessToken: accessToken,
        SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
      })
    );

    return { status: "SUCCESS" };
  } catch (error) {
    if (error.name === "EnableSoftwareTokenMFAException" || error.name === "CodeMismatchException") {
      throw new Error("Incorrect code. Please try again.");
    }
    if (error.name === "ExpiredCodeException") {
      throw new Error("The code has expired. Please enter a fresh one.");
    }
    if (error.name === "NotAuthorizedException") {
      throw new Error("Your session has expired. Please sign in again.");
    }
    throw new Error(error.message || "Could not enable two-factor authentication.");
  }
}

// Disable TOTP authentication
export async function disableSoftwareToken({ region, accessToken }) {
  try {
    const client = new CognitoIdentityProviderClient({ region });

    await client.send(
      new SetUserMFAPreferenceCommand({
        AccessToken: accessToken,
        SoftwareTokenMfaSettings: { Enabled: false, PreferredMfa: false },
      })
    );

    return { status: "SUCCESS" };
  } catch (error) {
    if (error.name === "NotAuthorizedException") {
      throw new Error("Your session has expired. Please sign in again.");
    }
    throw new Error(error.message || "Could not disable two-factor authentication.");
  }
}

/**
 * Get user details from Cognito using an access token.
 */
export async function getUserDetails({ region, accessToken }) {
  const client = new CognitoIdentityProviderClient({ region });

  const res = await client.send(
    new GetUserCommand({
      AccessToken: accessToken,
    })
  );

  const attributes =
    res.UserAttributes?.reduce((acc, attr) => {
      acc[attr.Name] = attr.Value;
      return acc;
    }, {}) ?? {};

  return {
    username: res.Username,
    attributes,               // { email, email_verified, sub, ... }
    mfaOptions: res.MFAOptions,
    preferredMfaSetting: res.PreferredMfaSetting,
    userMFASettingList: res.UserMFASettingList,
    raw: res,
  };
}
